import React from 'react';

import { type IntlShape } from 'react-intl-next';
import Loadable from 'react-loadable';

import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import {
	CommonLayout,
	type CustomSitesExtensionProviderProps,
	getExtensionManifest,
	type HubDefaultParameters,
} from '@atlassian/company-hub-common';

import { CAROUSEL_VERSION_2, carouselExtensionKey, carouselExtensionType } from '../../constants';
import {
	carouselNewParametersV2,
	createNewFrameWithDefaultParameters,
	FRAME_V2_DEFAULT_IMAGE_SRC,
} from '../../controllers/use-carousel-extension-parameters-and-actions';
import type { CarouselExtensionProps, CarouselParameters } from '../../types';

import { messages } from './messages';

const CarouselExtension = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_CarouselExtension" */ '../carousel-extension/editor'
			)
		).CarouselExtension,
	loading: () => <UFOLoadHold name="carousel-extension-loader"></UFOLoadHold>,
});

const carouselFrames = [
	{
		imageSrc: FRAME_V2_DEFAULT_IMAGE_SRC,
		layout: CommonLayout.SPLIT_IMAGE_LEFT,
	},
	{
		imageSrc: FRAME_V2_DEFAULT_IMAGE_SRC,
		layout: CommonLayout.FRAME_ABOVE_IMAGE,
	},
	{
		imageSrc: FRAME_V2_DEFAULT_IMAGE_SRC,
		layout: CommonLayout.SPLIT_IMAGE_RIGHT,
	},
];
const getV2Frames = ({
	intl,
	defaultParameters,
}: {
	defaultParameters?: HubDefaultParameters;
	intl: IntlShape;
}) => {
	return carouselFrames.map(({ imageSrc, layout }) =>
		createNewFrameWithDefaultParameters({
			intl,
			defaultParameters,
			defaultField: { imageSrc, layout },
		}),
	);
};

export const carouselParameters = ({
	extensionTitle,
	intl,
	defaultParameters,
}: {
	defaultParameters?: HubDefaultParameters;
	extensionTitle: string;
	intl: IntlShape;
}) => ({
	cards: getV2Frames({ intl, defaultParameters }),
	extensionTitle,
	version: CAROUSEL_VERSION_2,
	...carouselNewParametersV2,
});

export const carouselExtensionProvider = ({
	contentId,
	propsProvider,
	defaultParameters,
}: CarouselExtensionProps) => {
	return async ({
		editorActions,
		intl,
		openConfigPanel,
		setUpsellModalSource,
	}: CustomSitesExtensionProviderProps) => {
		const parameters = carouselParameters({
			extensionTitle: intl.formatMessage(messages.carouselMacroTitle),
			intl,
			defaultParameters,
		}) as CarouselParameters;

		return new DefaultExtensionProvider([
			getExtensionManifest<CarouselParameters>({
				title: intl.formatMessage(messages.carouselMacroTitleUpdated),
				type: carouselExtensionType,
				key: carouselExtensionKey,
				description: intl.formatMessage(messages.carouselMacroDescription),
				icons: {
					'48': () =>
						import(
							/* webpackChunkName: "loadable-CarouselExtensionIcon" */
							'../assets/carousel-extension-icon'
						).then((mod) => mod.CarouselExtensionIcon),
				},
				parameters,
				update: async () => {
					const selectedNode = editorActions?.getSelectedNode()?.toJSON();
					const selectedLocalId = selectedNode?.attrs?.localId;

					if (!!selectedLocalId && openConfigPanel) {
						openConfigPanel(selectedLocalId);
					}
				},
				render:
					async () =>
					({ node }: any) => {
						return (
							<CarouselExtension
								propsProvider={propsProvider}
								extensionNode={node}
								contentId={contentId}
							/>
						);
					},
				setUpsellModalSource,
				source: 'premiumCarouselInsert',
			}),
		]);
	};
};
